/**
 * Window Manager Context
 * Manages all open OS windows: open, close, focus, minimize, maximize, drag, resize
 */
import { createContext, useCallback, useContext, useState } from 'react'
import type { OSWindowState, WindowManagerContextValue } from './types'

const WindowManagerContext = createContext<WindowManagerContextValue | null>(null)

let zCounter = 100

export function WindowManagerProvider({ children }: { children: React.ReactNode }) {
  const [windows, setWindows] = useState<OSWindowState[]>([])

  const openWindow = useCallback((config: Omit<OSWindowState, 'zIndex' | 'minimized' | 'maximized'>) => {
    setWindows(prev => {
      // Already open? Focus it
      const existing = prev.find(w => w.id === config.id)
      if (existing) {
        zCounter++
        return prev.map(w => w.id === config.id
          ? { ...w, minimized: false, zIndex: zCounter }
          : w
        )
      }
      zCounter++
      return [...prev, { ...config, zIndex: zCounter, minimized: false, maximized: false }]
    })
  }, [])

  const closeWindow = useCallback((id: string) => {
    setWindows(prev => prev.filter(w => w.id !== id))
  }, [])

  const focusWindow = useCallback((id: string) => {
    setWindows(prev => {
      zCounter++
      return prev.map(w => w.id === id ? { ...w, zIndex: zCounter } : w)
    })
  }, [])

  const minimizeWindow = useCallback((id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? { ...w, minimized: true } : w))
  }, [])

  const maximizeWindow = useCallback((id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? { ...w, maximized: true } : w))
  }, [])

  const restoreWindow = useCallback((id: string) => {
    setWindows(prev => {
      zCounter++
      return prev.map(w => w.id === id ? { ...w, minimized: false, maximized: false, zIndex: zCounter } : w)
    })
  }, [])

  const updatePosition = useCallback((id: string, pos: { x: number; y: number }) => {
    setWindows(prev => prev.map(w => w.id === id ? { ...w, position: pos } : w))
  }, [])

  const updateSize = useCallback((id: string, size: { width: number; height: number }) => {
    setWindows(prev => prev.map(w => w.id === id ? { ...w, size } : w))
  }, [])

  return (
    <WindowManagerContext.Provider value={{
      windows, openWindow, closeWindow, focusWindow,
      minimizeWindow, maximizeWindow, restoreWindow,
      updatePosition, updateSize,
    }}>
      {children}
    </WindowManagerContext.Provider>
  )
}

export function useWindowManager() {
  const ctx = useContext(WindowManagerContext)
  if (!ctx) { throw new Error('useWindowManager must be inside WindowManagerProvider') }
  return ctx
}
